import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute, Params } from '@angular/router';
import {
  InspectionPointService,
  InspectionPluginService,
  LanguageService,
  MultiEditService,
  SessionDetectService
} from '../../shared/services/index';
import { InspectionPoint, Inspection, InspectionSession } from '../../shared/models/index';
import { BasePageComponent } from '../../base-page.component';
import { OrderSummariesComponent } from '../../utils/index';
import { ConfirmComponent } from '../../modal/confirm.component';

import * as _ from 'lodash';
import { PointMenuComponent } from '../../menus/point.menu';
import { environment } from '../../../environments/environment';
import { LogService } from '../../shared/services/log.service';
import { Subscription } from 'rxjs';

@Component({
  templateUrl: './detail.component.html',
  standalone: false
})
export class PointDetailPageComponent extends BasePageComponent implements OnInit, OnDestroy {
  public point: InspectionPoint;
  inspectionId: string;
  pointId: string;
  showHelp = false;
  public processing = false;
  loading = false;
  inspection: Inspection;
  multiEditSubscription: Subscription;
  @ViewChild('orderPlugins', { static: true }) orderPluginModal: OrderSummariesComponent;
  @ViewChild('deleteConfirm', { static: true }) deleteConfirm: ConfirmComponent;
  @ViewChild('saveConfirm', { static: true }) saveConfirm: ConfirmComponent;

  constructor(
    location: Location,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private pointService: InspectionPointService,
    private pluginService: InspectionPluginService,
    private language: LanguageService,
    private logService: LogService,
    private multiEditService: MultiEditService,
    private sessionDetect: SessionDetectService
  ) {
    super(location);
  }

  ngOnInit(): void {
    this.initRoute();
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) this.router.navigate([this.inspectionsRoute]);

      this.inspectionId = id;

      const pointId = params['pointId'];
      if (pointId == null) this.router.navigate([this.inspectionsRoute, this.inspectionId]);

      this.pointId = pointId;

      this.initData();
      this.multiEditSubscription = this.multiEditService.pointUpdate(this.pointId).subscribe(() => this.initData());
    });
  }

  initData(): void {
    this.pointService.getPoint(this.inspectionId, this.pointId).subscribe((data) => {
      this.point = data;
      this.inspection = data.inspection;
      this.pluginService.updatePluginsPostUrl(this.inspectionId, this.point.plugins);

      if (this.point.inspection.parts) this.point.inspection.parts.point = this.point;
    });
  }

  public openMenu(): void {
    this.modal.modalFooter = false;
    this.modal.modalHeader = false;
    this.modal.modalMessage = false;
    this.modal.open(PointMenuComponent);
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        this.gotoPropertiesPage();
        break;
      case 2:
        this.gotoAddPluginPage();
        break;
      case 3:
        this.gotoNotAvailablePoint();
        break;
      case 4:
        this.updateShowHelp();
        break;
      case 5:
        this.orderPlugins();
        break;
      case 6:
        this.openDeleteConfirm();
        break;
      case 7:
        this.gotoEditJsonPage();
        break;
      default:
    }
  }

  public updateShowHelp() {
    this.showHelp = !this.showHelp;
    this.modal.cmpRef.instance.updateShowHelpText(!this.showHelp);
  }

  public gotoPropertiesPage() {
    return this.router.navigate([this.inspectionsRoute, this.point.inspection.id, 'points', this.point.id, 'edit']);
  }

  public gotoAddPluginPage() {
    return this.router.navigate([
      this.inspectionsRoute,
      this.point.inspection.id,
      'points',
      this.point.id,
      'addPlugin',
      this.inspection.applicationSubType || ''
    ]);
  }

  public gotoNotAvailablePoint() {
    return this.router.navigate([this.inspectionsRoute, this.point.inspection.id, 'points', this.point.id, 'notavailable']);
  }

  public gotoEditJsonPage() {
    return this.router.navigate([this.inspectionsRoute, this.point.inspection.id, 'points', this.point.id, 'editjson']);
  }

  updateStatus(point: InspectionPoint): void {
    this.processing = true;
    if (point.status === InspectionPoint.NotApplicable) {
      this.pointService.restorePoint(point.inspection.id, point.id).subscribe(
        (el) => {
          this.processing = false;
          this.point = el;
          this.pointService.initPointPlugins(this.point);
          this.goToBack();
        },
        (err) => this.handleError(err)
      );
    } else {
      const newStatus = point.status === InspectionPoint.Done ? InspectionPoint.Draft : InspectionPoint.Done;
      this.pointService.updateStatus(point.inspection.id, point.id, newStatus).subscribe(
        (el) => {
          this.processing = false;
          this.point = el;
          this.goToBack();
        },
        (err) => this.handleError(err)
      );
    }
  }

  public orderPlugins() {
    const orderableItems = _.map(this.point.plugins, (p: any) => ({
      id: p.id,
      name: this.language.getInstantValue(`plugins.${p.pluginType}.name`) || p.name || p.pluginType,
      type: 'plugin',
      order: p.order
    }));

    this.orderPluginModal.open(orderableItems);
  }

  savePluginsOrder(plugins: any) {
    const orderableItems = _.map(plugins, (p: any) => ({
      id: p.id,
      order: p.order
    }));

    this.pointService.orderPlugins(this.inspectionId, this.pointId, orderableItems).subscribe((el) => {
      // this.point.plugins = plugins;
    });
  }

  cancel(): void {
    this.goToBack();
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  deletePoint(e: any) {
    this.pointService.deletePoints(this.inspectionId, [this.pointId]).subscribe((deletedPointIds) => {
      if (deletedPointIds) {
        this.goToBack();
      }
    });
  }

  public goToBackList() {
    if (this.point.status !== InspectionPoint.Done && this.pointService.isPointChanged(this.pointId)) {
      this.openSaveChangesConfirm();
    } else {
      this.goToBack();
    }
  }

  public dontSaveChanges() {
    this.pointService.cancelChanges(this.pointId);
    this.pointService.initPointPlugins(this.point);
    this.goToBack();
  }

  public openSaveChangesConfirm() {
    this.saveConfirm.open();
  }

  public saveChanges(e: any) {
    this.processing = true;
    this.pointService.updateStatus(this.point.inspection.id, this.point.id, 100).subscribe(
      (el) => {
        this.processing = false;
        this.point = el;
        this.goToBack();
      },
      (err) => this.handleError(err)
    );
  }

  public handleError(err) {
    this.logService.logError(err, 'handleerror');
    this.processing = false;
    if (window.confirm('unable to update status, reload inspection page ?')) {
      // this.router.navigate([environment.applicationRoute, 'inspections', this.inspectionId]);
      window.location.href = `/${environment.applicationRoute}/inspections/${this.inspectionId}`;
    }
  }

  navigateToSession(session: InspectionSession) {
    this.sessionDetect.navigate(session);
  }

  ngOnDestroy() {
    this.multiEditSubscription?.unsubscribe();
  }
}
